// Airline reference data for the frontend display components.
//
// Most travelers do not recognize 2-letter IATA codes (WN=Southwest, NK=Spirit,
// B6=JetBlue, F9=Frontier, AS=Alaska, ...). These entries let us surface the full
// airline name + brand color + a logo with a graceful, always-renders fallback.
//
// This is a self-contained frontend dataset — it intentionally does NOT import the
// backend `airportData.ts`. Logo URLs mirror the backend's gstatic convention but
// every consumer must tolerate the image failing to load (offline / blocked / 404)
// and fall back to a brand-colored monogram chip.

export interface AirlineInfo {
  /** Full marketing name, e.g. "Southwest Airlines". */
  name: string;
  /** Brand color (hex) used for the monogram-chip fallback + accents. */
  brandColor: string;
  /** A short monogram for the fallback chip (1-3 chars), e.g. "AA", "B6". */
  monogram: string;
  /**
   * Remote logo URL (Google Flights' public airline-logo CDN — same source the
   * backend uses). Optional: when absent, or when the image fails to load, the
   * brand-colored monogram chip is shown instead. Never hard-depend on this.
   */
  logo?: string;
}

// Google Flights public airline logo CDN (no hosting required, license-safe via
// hotlink the way Google Flights itself serves them). Always paired with a
// monogram fallback so a failed fetch never leaves the UI blank.
const gstatic = (iata: string): string =>
  `https://www.gstatic.com/flights/airline_logos/70px/${iata}.png`;

export const AIRLINES: Record<string, AirlineInfo> = {
  // US majors + low-cost carriers seen across the app
  AA: { name: "American Airlines", brandColor: "#0078D2", monogram: "AA", logo: gstatic("AA") },
  DL: { name: "Delta Air Lines", brandColor: "#C8102E", monogram: "DL", logo: gstatic("DL") },
  UA: { name: "United Airlines", brandColor: "#005DAA", monogram: "UA", logo: gstatic("UA") },
  WN: { name: "Southwest Airlines", brandColor: "#304CB2", monogram: "WN", logo: gstatic("WN") },
  AS: { name: "Alaska Airlines", brandColor: "#01426A", monogram: "AS", logo: gstatic("AS") },
  B6: { name: "JetBlue", brandColor: "#003876", monogram: "B6", logo: gstatic("B6") },
  NK: { name: "Spirit Airlines", brandColor: "#FFEC00", monogram: "NK", logo: gstatic("NK") },
  F9: { name: "Frontier Airlines", brandColor: "#00854A", monogram: "F9", logo: gstatic("F9") },
  HA: { name: "Hawaiian Airlines", brandColor: "#5B2A86", monogram: "HA", logo: gstatic("HA") },
  G4: { name: "Allegiant Air", brandColor: "#005DAA", monogram: "G4", logo: gstatic("G4") },
  SY: { name: "Sun Country Airlines", brandColor: "#003DA5", monogram: "SY", logo: gstatic("SY") },
  MX: { name: "Breeze Airways", brandColor: "#00A9E0", monogram: "MX", logo: gstatic("MX") },
  // Common international carriers
  AC: { name: "Air Canada", brandColor: "#D8202F", monogram: "AC", logo: gstatic("AC") },
  WS: { name: "WestJet", brandColor: "#0F6FBE", monogram: "WS", logo: gstatic("WS") },
  BA: { name: "British Airways", brandColor: "#075AAA", monogram: "BA", logo: gstatic("BA") },
  LH: { name: "Lufthansa", brandColor: "#05164D", monogram: "LH", logo: gstatic("LH") },
  AF: { name: "Air France", brandColor: "#002157", monogram: "AF", logo: gstatic("AF") },
  EK: { name: "Emirates", brandColor: "#D71921", monogram: "EK", logo: gstatic("EK") },
  QF: { name: "Qantas", brandColor: "#E40000", monogram: "QF", logo: gstatic("QF") },
};

const DEFAULT_BRAND_COLOR = "#0A7A72"; // instrument teal — the TripMuscle accent

/**
 * Resolve display info for an airline code. For unknown / empty codes returns a
 * safe object whose `name` is the raw code (so the UI always has something to
 * render) and `logo` is undefined (forcing the monogram fallback).
 */
export function getAirlineInfo(code: string | null | undefined): AirlineInfo {
  const key = (code || "").trim().toUpperCase();
  const found = AIRLINES[key];
  if (found) return found;
  return {
    name: key || "Unknown airline",
    brandColor: DEFAULT_BRAND_COLOR,
    monogram: key.slice(0, 2) || "?",
    logo: undefined,
  };
}

/** Convenience: full airline name for a code (raw code if unknown). */
export function getAirlineName(code: string | null | undefined): string {
  const key = (code || "").trim().toUpperCase();
  return AIRLINES[key]?.name ?? (key || "");
}

/** True when we have curated brand data (name/color/logo) for this code. */
export function isKnownAirline(code: string | null | undefined): boolean {
  const key = (code || "").trim().toUpperCase();
  return Boolean(AIRLINES[key]);
}
